/**
 * CascadeDialog — modal for confirming cascade changes.
 *
 * UP:   operator picks an impianto from a different edificio → update edificio+amministratore
 * DOWN: operator picks a different edificio → show impianti of new edificio
 *       - 1 impianto → auto-selected, confirm
 *       - N impianti → operator picks one
 *       - 0 impianti → warning
 */
import { useEffect, useRef, useState } from "react";
import { fetchEntityChildren } from "../api/client";
import type { EntitySearchResult } from "../api/types";
import { stripPkPrefix } from "../helpers/entityUtils";

interface CascadeDialogProps {
  open: boolean;
  type: "UP" | "DOWN";
  fromEdificio?: { pk: string; label: string } | null;
  toEdificio?: { pk: string; label: string } | null;
  newImpianto?: { pk: string; label: string } | null;
  newAmministratore?: { pk: string; label: string } | null;
  currentImpianto?: { pk: string; label: string } | null;
  /** For DOWN: callback with picked impianto (null = no impianti available) */
  onConfirmWithImpianto?: (impianto: EntitySearchResult | null) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export function CascadeDialog({
  open, type, fromEdificio, toEdificio, newImpianto, newAmministratore, currentImpianto,
  onConfirmWithImpianto, onConfirm, onCancel,
}: CascadeDialogProps) {
  const isDown = type === "DOWN";
  const [impianti, setImpianti] = useState<EntitySearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPk, setSelectedPk] = useState<string | null>(null);
  const backdropMouseDown = useRef(false);

  // Fetch impianti for new edificio (DOWN only)
  useEffect(() => {
    if (!open || !isDown || !toEdificio?.pk) { setImpianti([]); setSelectedPk(null); return; }
    let cancelled = false;
    setLoading(true);
    fetchEntityChildren("edificio", toEdificio.pk, "impianto")
      .then((res) => {
        if (cancelled) return;
        setImpianti(res);
        // Auto-select if only 1
        setSelectedPk(res.length === 1 ? res[0].pk : null);
      })
      .catch(() => { if (!cancelled) setImpianti([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, isDown, toEdificio?.pk]);

  if (!open) return null;

  function handleConfirmDown() {
    if (onConfirmWithImpianto) {
      const picked = impianti.find((i) => i.pk === selectedPk) ?? null;
      onConfirmWithImpianto(picked);
    } else {
      onConfirm();
    }
  }

  const canConfirmDown = impianti.length === 0 || !!selectedPk;

  return (
    <div
      className="dialog-overlay"
      onMouseDown={(e) => { backdropMouseDown.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (e.target === e.currentTarget && backdropMouseDown.current) onCancel(); }}
    >
      <div className="dialog-card" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="dialog-header" style={{ alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: "var(--border-radius-lg)", flexShrink: 0,
              background: isDown ? "var(--indaco-warning-light)" : "var(--indaco-brand-primary-light)",
              color: isDown ? "var(--indaco-warning)" : "var(--indaco-brand-primary)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 22 }}>{isDown ? "swap_horiz" : "cable"}</span>
            </div>
            <div>
              <div style={{ fontSize: "var(--font-size-md)", fontWeight: 600 }}>
                {isDown ? "Cambio edificio" : "Propagazione a cascata"}
              </div>
              <div style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-700)", marginTop: 3, lineHeight: 1.45 }}>
                {isDown
                  ? "L'impianto attuale non appartiene al nuovo edificio. Seleziona l'impianto corretto."
                  : "L'impianto selezionato appartiene a un altro edificio. Confermando, si aggiornano anche edificio e amministratore."}
              </div>
            </div>
          </div>
        </div>

        {/* Edificio change */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 10 }}>
          <CascadeLine icon="apartment" label="Edificio"
            from={fromEdificio?.label} to={toEdificio?.label} />
        </div>

        {/* Impianto section */}
        <div style={{ marginBottom: 4 }}>
          {isDown ? (
            <div>
              <div style={{ fontSize: "var(--font-size-xs)", fontWeight: 600, color: "var(--indaco-gray-500)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-md)" }}>engineering</span>
                Impianti del nuovo edificio
                {!loading && <span style={{ fontWeight: 400, textTransform: "none" }}>({impianti.length})</span>}
              </div>

              {loading ? (
                <div style={{ padding: "12px 0", color: "var(--indaco-gray-500)", fontSize: "var(--font-size-sm)" }}>Caricamento impianti…</div>
              ) : impianti.length === 0 ? (
                <div style={{
                  padding: "10px 12px", borderRadius: "var(--border-radius-md)", fontSize: "var(--font-size-sm)",
                  background: "var(--indaco-warning-light)", color: "var(--indaco-warning)",
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-md)" }}>warning</span>
                  Nessun impianto associato a questo edificio
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 200, overflowY: "auto" }}>
                  {impianti.map((imp) => {
                    const isSel = imp.pk === selectedPk;
                    const descTipo = String(imp.fields?.desc_tipo ?? "");
                    const desc = imp.label || imp.pk;
                    return (
                      <label key={imp.pk} style={{
                        display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                        borderRadius: "var(--border-radius-md)", cursor: "pointer",
                        border: isSel ? "2px solid var(--indaco-brand-primary)" : "1px solid var(--indaco-gray-200)",
                        background: isSel ? "var(--indaco-brand-primary-light)" : "var(--indaco-white)",
                        transition: "all 0.15s",
                      }}>
                        <input type="radio" name="cascade-impianto" checked={isSel}
                          onChange={() => setSelectedPk(imp.pk)}
                          style={{ accentColor: "var(--indaco-brand-primary)", margin: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "var(--font-size-sm)", fontWeight: isSel ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{desc}</div>
                          <div style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-500)" }}>
                            {stripPkPrefix(imp.pk)}
                            {descTipo && descTipo !== "Non Definito" && <span> · {descTipo}</span>}
                          </div>
                        </div>
                        {impianti.length === 1 && (
                          <span style={{ fontSize: "var(--font-size-xs)", fontWeight: 600, color: "var(--indaco-success)", textTransform: "uppercase" }}>unico</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Old impianto strikethrough */}
              {currentImpianto && (
                <div style={{ marginTop: 8, fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-400)" }}>
                  <span style={{ textDecoration: "line-through" }}>{currentImpianto.label}</span>
                  <span style={{ marginLeft: 6, fontSize: "var(--font-size-xs)" }}>precedente</span>
                </div>
              )}
            </div>
          ) : (
            <CascadeLine icon="engineering" label="Impianto" to={newImpianto?.label} />
          )}

          {/* Amministratore */}
          <div style={{ marginTop: 10 }}>
            <CascadeLine icon="admin_panel_settings" label="Amministratore · derivato"
              to={newAmministratore?.label ?? "— nessuno associato"} muted={!newAmministratore} />
          </div>
        </div>

        {/* Actions */}
        <div className="dialog-footer">
          <button className="btn btn-outline" onClick={onCancel}>Annulla</button>
          {isDown ? (
            <button className="btn btn-primary" disabled={loading || !canConfirmDown} onClick={handleConfirmDown}>
              <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-md)", marginRight: 4 }}>check</span>
              {impianti.length === 0 ? "Conferma senza impianto" :
               impianti.length === 1 ? "Conferma" : "Conferma con impianto selezionato"}
            </button>
          ) : (
            <button className="btn btn-primary" onClick={onConfirm}>
              <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-md)", marginRight: 4 }}>bolt</span>
              Aggiorna tutto
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CascadeLine({ icon, label, from, to, muted }: {
  icon: string; label: string; from?: string | null; to?: string | null; muted?: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ width: 28, display: "flex", justifyContent: "center" }}>
        <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-lg)", color: "var(--indaco-gray-600)" }}>{icon}</span>
      </div>
      <div style={{ flex: 1, fontSize: "var(--font-size-sm)" }}>
        <div style={{ color: "var(--indaco-gray-500)", fontSize: "var(--font-size-xs)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
        <div style={{ marginTop: 2 }}>
          {from && <span style={{ textDecoration: "line-through", color: "var(--indaco-gray-500)" }}>{from}</span>}
          {from && to && <span className="material-symbols-outlined" style={{ fontSize: "var(--font-size-base)", margin: "0 6px", verticalAlign: "middle", color: "var(--indaco-gray-400)" }}>arrow_forward</span>}
          {to ? (
            <span style={{ fontWeight: 600, color: muted ? "var(--indaco-gray-500)" : "var(--indaco-brand-primary)" }}>{to}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
